import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { AdminLayout } from '@/components/AdminLayout';
import { Plus, Store, ExternalLink, Ban, Check, User, Trash2, Edit2, Key, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const AdminStores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeUsers, setStoreUsers] = useState([]);
  const [form, setForm] = useState({
    storeName: '',
    ownerEmail: '',
    ownerPassword: '',
    ownerName: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    slug: '',
    whatsapp: ''
  });
  const [userEditForm, setUserEditForm] = useState({
    userId: '',
    email: '',
    name: '',
    newPassword: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const res = await axios.get(`${API}/admin/stores`);
      setStores(res.data);
    } catch (e) {
      toast.error('Erro ao carregar lojas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStore = async () => {
    if (!form.storeName || !form.ownerEmail || !form.ownerPassword || !form.ownerName) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      const storeRes = await axios.post(`${API}/admin/stores`, {
        name: form.storeName,
        slug: form.storeName.toLowerCase().replace(/\s+/g, '-'),
        whatsapp: '',
        is_active: true
      });

      await axios.post(`${API}/admin/users`, {
        email: form.ownerEmail,
        password: form.ownerPassword,
        name: form.ownerName,
        role: 'store_admin',
        store_id: storeRes.data.id
      });

      toast.success('Loja e usuário criados com sucesso!');
      setShowCreateModal(false);
      setForm({ storeName: '', ownerEmail: '', ownerPassword: '', ownerName: '' });
      fetchStores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar loja');
    }
  };

  const handleToggleStatus = async (storeId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/stores/${storeId}/status?is_active=${!currentStatus}`);
      toast.success('Status atualizado');
      fetchStores();
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteStore = async (store) => {
    if (!confirm(`Tem certeza que deseja excluir a loja "${store.name}"?\n\nIsso irá remover TODOS os dados relacionados (produtos, pedidos, usuários, etc.) permanentemente.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/stores/${store.id}`);
      toast.success('Loja excluída com sucesso');
      fetchStores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir loja');
    }
  };

  const openEditModal = (store) => {
    setSelectedStore(store);
    setEditForm({
      name: store.name,
      slug: store.slug,
      whatsapp: store.whatsapp || ''
    });
    setShowEditModal(true);
  };

  const handleEditStore = async () => {
    if (!editForm.name || !editForm.slug) {
      toast.error('Nome e slug são obrigatórios');
      return;
    }

    try {
      await axios.put(`${API}/admin/stores/${selectedStore.id}`, editForm);
      toast.success('Loja atualizada com sucesso');
      setShowEditModal(false);
      fetchStores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar loja');
    }
  };

  const openUserModal = async (store) => {
    setSelectedStore(store);
    try {
      const res = await axios.get(`${API}/admin/stores/${store.id}/users`);
      setStoreUsers(res.data);
      setUserEditForm({ userId: '', email: '', name: '', newPassword: '' });
      setShowUserModal(true);
    } catch (e) {
      toast.error('Erro ao carregar usuários');
    }
  };

  const selectUserToEdit = (user) => {
    setUserEditForm({
      userId: user.id,
      email: user.email,
      name: user.name || '',
      newPassword: ''
    });
  };

  const handleUpdateUser = async () => {
    if (!userEditForm.userId) {
      toast.error('Selecione um usuário para editar');
      return;
    }

    try {
      const updateData = {};
      if (userEditForm.email) updateData.email = userEditForm.email;
      if (userEditForm.name) updateData.name = userEditForm.name;
      if (userEditForm.newPassword) updateData.password = userEditForm.newPassword;

      await axios.put(`${API}/admin/users/${userEditForm.userId}`, updateData);
      toast.success('Usuário atualizado com sucesso');
      
      // Refresh users list
      const res = await axios.get(`${API}/admin/stores/${selectedStore.id}/users`);
      setStoreUsers(res.data);
      setUserEditForm({ userId: '', email: '', name: '', newPassword: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar usuário');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Lojas">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Lojas">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-white/60">{stores.length} lojas cadastradas</p>
          <Button onClick={() => setShowCreateModal(true)} className="bg-[#ff6b00] hover:bg-[#ff8533]">
            <Plus className="w-4 h-4 mr-2" />
            Nova Loja
          </Button>
        </div>

        <Card className="card-adegga">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-white/60">Loja</TableHead>
                <TableHead className="text-white/60">Slug</TableHead>
                <TableHead className="text-white/60">Status</TableHead>
                <TableHead className="text-white/60">Criada em</TableHead>
                <TableHead className="text-white/60 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stores.map(store => (
                <TableRow key={store.id} className="border-white/5">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ff6b00]/20 flex items-center justify-center">
                        <Store className="w-5 h-5 text-[#ff6b00]" />
                      </div>
                      <span className="text-white font-medium">{store.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[#ff6b00] font-mono">{store.slug}</TableCell>
                  <TableCell>
                    <Badge className={store.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {store.is_active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-white/60">
                    {new Date(store.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <a href={`/${store.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="ghost" className="text-white/60" title="Ver loja">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-blue-400"
                        onClick={() => openEditModal(store)}
                        title="Editar loja"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-purple-400"
                        onClick={() => openUserModal(store)}
                        title="Gerenciar usuários"
                      >
                        <User className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className={store.is_active ? 'text-yellow-400' : 'text-green-400'}
                        onClick={() => handleToggleStatus(store.id, store.is_active)}
                        title={store.is_active ? 'Desativar' : 'Ativar'}
                      >
                        {store.is_active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400"
                        onClick={() => handleDeleteStore(store)}
                        title="Excluir loja"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {stores.length === 0 && (
            <div className="text-center py-12 text-white/40">
              Nenhuma loja cadastrada
            </div>
          )}
        </Card>

        {/* Create Store Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Nova Loja</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/60 mb-1 block">Nome da Loja</label>
                <Input
                  placeholder="Ex: Adega do João"
                  value={form.storeName}
                  onChange={(e) => setForm({ ...form, storeName: e.target.value })}
                  className="bg-[#0f0f15] border-white/10 text-white"
                />
              </div>
              
              <div className="border-t border-white/10 pt-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#ff6b00]" />
                  Dados do Lojista
                </h4>
                <div className="space-y-3">
                  <Input
                    placeholder="Nome do lojista"
                    value={form.ownerName}
                    onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                  <Input
                    type="email"
                    placeholder="Email de acesso"
                    value={form.ownerEmail}
                    onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                  <Input
                    type="password"
                    placeholder="Senha"
                    value={form.ownerPassword}
                    onChange={(e) => setForm({ ...form, ownerPassword: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleCreateStore}>
                Criar Loja
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Store Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Editar Loja</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/60 mb-1 block">Nome da Loja</label>
                <Input
                  placeholder="Nome da loja"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-[#0f0f15] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-1 block">Slug (URL)</label>
                <Input
                  placeholder="slug-da-loja"
                  value={editForm.slug}
                  onChange={(e) => setEditForm({ ...editForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  className="bg-[#0f0f15] border-white/10 text-white font-mono"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-1 block">WhatsApp</label>
                <Input
                  placeholder="(XX) XXXXX-XXXX"
                  value={editForm.whatsapp}
                  onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })}
                  className="bg-[#0f0f15] border-white/10 text-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowEditModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleEditStore}>
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Management Modal */}
        <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
          <DialogContent className="bg-[#13131f] border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                Gerenciar Usuários - {selectedStore?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Users List */}
              <div className="space-y-2">
                <label className="text-sm text-white/60">Usuários da Loja</label>
                {storeUsers.length === 0 ? (
                  <p className="text-white/40 text-sm">Nenhum usuário encontrado</p>
                ) : (
                  <div className="space-y-2">
                    {storeUsers.map(user => (
                      <div
                        key={user.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          userEditForm.userId === user.id
                            ? 'bg-[#ff6b00]/20 border-[#ff6b00]'
                            : 'bg-[#0f0f15] border-white/10 hover:border-white/20'
                        }`}
                        onClick={() => selectUserToEdit(user)}
                      >
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-white/40" />
                          <span className="text-white">{user.email}</span>
                        </div>
                        {user.name && (
                          <p className="text-white/60 text-sm mt-1">{user.name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Edit Form */}
              {userEditForm.userId && (
                <div className="border-t border-white/10 pt-4 space-y-3">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <Key className="w-4 h-4 text-[#ff6b00]" />
                    Editar Usuário Selecionado
                  </h4>
                  <Input
                    type="email"
                    placeholder="Novo email"
                    value={userEditForm.email}
                    onChange={(e) => setUserEditForm({ ...userEditForm, email: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                  <Input
                    placeholder="Nome"
                    value={userEditForm.name}
                    onChange={(e) => setUserEditForm({ ...userEditForm, name: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                  <Input
                    type="password"
                    placeholder="Nova senha (deixe vazio para manter)"
                    value={userEditForm.newPassword}
                    onChange={(e) => setUserEditForm({ ...userEditForm, newPassword: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                  <Button 
                    className="w-full bg-[#ff6b00] hover:bg-[#ff8533]" 
                    onClick={handleUpdateUser}
                  >
                    Atualizar Usuário
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowUserModal(false)}>Fechar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminStores;
